/**
 * The bag as a drawer (studio-shop D4, the Prints mockup's `.bag`): the bag's
 * standard look over a custom page, sliding in from the right while the
 * page's shop has it open. Escape, the scrim and Close close it; leaving the
 * bag for checkout closes it first. Portalled to the body, so no page layout
 * clips or stacks over it.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { pieceRoot } from '../../contract/runtime';
import { contract } from './contract';
import { BagBody } from './BagBody';
import type { ShopBagRuntime } from './useShopBag';
import css from './ShopBag.module.css';

/** How long the panel takes to slide away before it leaves the page. */
const CLOSE_MS = 380;

export function ShopBagDrawer(bag: ShopBagRuntime) {
  const { open } = bag.state;
  const { close } = bag.actions;
  const titleId = useId();
  const panel = useRef<HTMLElement>(null);
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  // The latest Close, so the open effects never re-run on a new one.
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (open) {
      setMounted(true);
      const frame = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(frame);
    }
    setShown(false);
    const timer = setTimeout(() => setMounted(false), CLOSE_MS);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    panel.current?.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus({ preventScroll: true });
    };
  }, [open]);

  // Drawn from the moment it opens (so its first button takes the focus) until it has slid away.
  if (!open && !mounted) return null;
  return createPortal(
    <div className={`${css.drawer} ${shown ? css.open : ''}`} {...pieceRoot(contract)}>
      <div className={css.scrim} onClick={close} />
      <aside ref={panel} className={css.panel} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <BagBody {...bag} titleId={titleId} />
      </aside>
    </div>,
    document.body,
  );
}
